import { Injectable } from '@angular/core';
import { EventData } from '../../vibration-events/models/event-data';
import { Feature } from 'ol';
import VectorSource from 'ol/source/Vector';
import MapConstants from '../models/map-constants';
import { FEATURE_TYPE } from '../models/map-enums';
import { transform } from 'ol/proj';
import Icon from 'ol/style/Icon';
import { Point } from 'ol/geom';
import { Fill, Stroke, Style, Text } from 'ol/style';
import { ControlPoint } from '../../../shared/models/controlPoint';
import CircleStyle from 'ol/style/Circle';
import { RecordMetadata } from '@shared/models/charts/record-metadata';

@Injectable({
  providedIn: 'root'
})
export class MapUtilsService {
  private readonly FEATURE_HAS_ALARM = 'hasAlarm';

  constructor() { }

  public createEventsFeatures(icon: Icon, vectorSource: VectorSource, events: EventData[]) {
    vectorSource.clear();
    events.forEach((event: EventData) => {
      const coord = transform([event.positionCoordinates.x, event.positionCoordinates.y], MapConstants.EPSG_WGS84, MapConstants.EPSG_REFERENCE_OL);
      const feature = new Feature(new Point(coord));
      feature.set(MapConstants.FEATURE_NAME, FEATURE_TYPE.event);
      feature.set(MapConstants.FEATURE_LABEL, event.name);
      feature.set(MapConstants.FEATURE_ID, event.id);
      const featureStyle = this.createStyle(icon, event.name);
      feature.setStyle(featureStyle);
      vectorSource.addFeature(feature);
    });
  }

  public createPointsFeatures(icon: Icon, vectorSourceBasePoint: VectorSource, points: ControlPoint[], alarmControlPointIds: number[] = [], alarmIcon?: Icon) {
    vectorSourceBasePoint.clear();
    const alarmSet = new Set(alarmControlPointIds);
    points.forEach((point: ControlPoint) => {
      const coord = transform([point.x, point.y], MapConstants.EPSG_WGS84, MapConstants.EPSG_REFERENCE_OL);
      const feature = new Feature(new Point(coord));
      feature.set(MapConstants.FEATURE_NAME, FEATURE_TYPE.controlPoint);
      feature.set(MapConstants.FEATURE_LABEL, point.name);
      feature.set(MapConstants.FEATURE_ID, point.controlPointId);
      const hasAlarm = alarmSet.has(point.controlPointId);
      feature.set(this.FEATURE_HAS_ALARM, hasAlarm);
      const featureStyle = this.createControlPointStyles(icon, point.name, hasAlarm, alarmIcon, false);
      feature.setStyle(featureStyle);
      vectorSourceBasePoint.addFeature(feature);
    });
  }
  public createRecordsFeatures(icon: Icon, vectorSourceBasePoint: VectorSource, points: RecordMetadata[]) {
    vectorSourceBasePoint.clear();
    points.forEach((record: RecordMetadata) => {
      const coord = transform([record.longitude, record.latitude], MapConstants.EPSG_WGS84, MapConstants.EPSG_REFERENCE_OL);
      const feature = new Feature(new Point(coord));
      feature.set(MapConstants.FEATURE_NAME, FEATURE_TYPE.controlPoint);
      feature.set(MapConstants.FEATURE_LABEL, record.seismographSerialNumber);
      feature.set(MapConstants.FEATURE_ID, record.equipmentId);
      const featureStyle = this.createStyle(icon, record.seismographSerialNumber);
      feature.setStyle(featureStyle);
      vectorSourceBasePoint.addFeature(feature);
    });
  }

  public createStyle(icon: Icon, name: string, geometry: any | null = null): Style {
    return new Style({
      image: icon,
      text: new Text({
        text: name,
        font: '12px Arial, sans-serif',
        fill: new Fill({ color: 'black' }),
        offsetX: 32,
        textAlign: 'left',
        textBaseline: 'middle',
        backgroundFill: new Fill({
          color: 'rgba(255, 255, 255, 0.95)',
        }),
        padding: [5, 10, 3, 10],
      }),
    });
  }

  public createStyleWithCircle(icon: Icon, feature: Feature): Style[] {
    let featureName = feature.get(MapConstants.FEATURE_NAME);
    if(featureName === FEATURE_TYPE.event) {
        icon = new Icon({
        src: './icon/source_red.svg' ,
        scale: 1,
        });
    }

    const iconStyle = new Style({
      image: icon,
    });
    const circleStyle = new Style({
      image: new CircleStyle({
        radius: 20,
        fill: new Fill({ color: 'rgba(0, 0, 0, 0)' }),
        stroke: new Stroke({ color: '#FB5C61', width: 3 }),
      }),
      geometry: feature => feature.getGeometry(),
    });
    const labelStyle = this.createLabelOnlyStyle(feature.get(MapConstants.FEATURE_LABEL));
    return [iconStyle, circleStyle, labelStyle];
  }

  public applyStyleToFeature(icon: Icon, vectorSource: VectorSource, featureId: number, alarmIcon?: Icon) {
    const features = vectorSource.getFeatures();
    features.forEach(feature => {
      const hasAlarm = !!feature.get(this.FEATURE_HAS_ALARM);
      const featureType = feature.get(MapConstants.FEATURE_NAME);
      if (featureId === -1) {
        if (featureType === FEATURE_TYPE.controlPoint) {
          feature.setStyle(this.createControlPointStyles(icon, feature.get(MapConstants.FEATURE_LABEL), hasAlarm, alarmIcon, false));
        } else {
          feature.setStyle(this.createStyle(icon, feature.get(MapConstants.FEATURE_LABEL), feature.getGeometry()));
        }
      } else {
        if (feature.get(MapConstants.FEATURE_ID) === featureId) {
          if (featureType === FEATURE_TYPE.controlPoint) {
            feature.setStyle(this.createControlPointStyles(icon, feature.get(MapConstants.FEATURE_LABEL), hasAlarm, alarmIcon, true));
          } else {
            feature.setStyle(this.createStyleWithCircle(icon, feature));
          }
        } else {
          if (featureType === FEATURE_TYPE.controlPoint) {
            feature.setStyle(this.createControlPointStyles(icon, feature.get(MapConstants.FEATURE_LABEL), hasAlarm, alarmIcon, false));
          } else {
            feature.setStyle(this.createStyle(icon, feature.get(MapConstants.FEATURE_LABEL), feature.getGeometry()));
          }
        }
      }
    });
  }

  public updateControlPointAlarmState(
    vectorSource: VectorSource,
    alarmControlPointIds: number[],
    icon: Icon,
    alarmIcon: Icon,
    selectedFeatureId: number | null = null
  ) {
    const alarmSet = new Set(alarmControlPointIds);
    const features = vectorSource.getFeatures();

    features.forEach(feature => {
      const featureId = feature.get(MapConstants.FEATURE_ID);
      const hasAlarm = alarmSet.has(featureId);
      feature.set(this.FEATURE_HAS_ALARM, hasAlarm);

      if (selectedFeatureId != null && featureId === selectedFeatureId) {
        feature.setStyle(this.createControlPointStyles(icon, feature.get(MapConstants.FEATURE_LABEL), hasAlarm, alarmIcon, true));
      } else {
        feature.setStyle(this.createControlPointStyles(icon, feature.get(MapConstants.FEATURE_LABEL), hasAlarm, alarmIcon, false));
      }
    });
  }

  private createControlPointStyles(
    baseIcon: Icon,
    label: string,
    hasAlarm: boolean,
    alarmIcon?: Icon,
    withCircle: boolean = false
  ): Style[] {
    const styles: Style[] = [];

    styles.push(
      new Style({
        image: baseIcon,
      })
    );

    if (hasAlarm && alarmIcon) {
      const alarmSrc = alarmIcon.getSrc() || './icon/control_point_alarm_badge.svg';
      const alarmScale = alarmIcon.getScale() as number | undefined;

      styles.push(
        new Style({
          image: new Icon({
            src: alarmSrc,
            scale: alarmScale ?? 0.7,
            displacement: [0, 0],
          }),
        })
      );
    }

    if (withCircle) {
      styles.push(
        new Style({
          image: new CircleStyle({
            radius: 20,
            fill: new Fill({ color: 'rgba(0, 0, 0, 0)' }),
            stroke: new Stroke({ color: '#FB5C61', width: 3 }),
          }),
        })
      );
    }

    styles.push(this.createLabelOnlyStyle(label));

    return styles;
  }

  private createLabelOnlyStyle(name: string): Style {
    return new Style({
      text: new Text({
        text: name,
        font: '12px Arial, sans-serif',
        fill: new Fill({ color: 'black' }),
        offsetX: 32,
        textAlign: 'left',
        textBaseline: 'middle',
        backgroundFill: new Fill({
          color: 'rgba(255, 255, 255, 0.95)',
        }),
        padding: [5, 10, 3, 10],
      }),
    });
  }

  public findFeatureById(vectorSource: VectorSource, id: number): Feature | null {
    const features = vectorSource.getFeatures();
    return features.find(feature => feature.get(MapConstants.FEATURE_ID) === id) || null;
  }


}
